import {AddressType, DateOnlyIsoString, DateTimeIsoString, Pronoun, SelectOptions, State} from '@aviary/store/common/common.types';
import {Branch} from '@store/branches/branch.types';
import {HoldStatus} from '@store/holds/holds.types';

export const PATRON_FEATURE_KEY = 'patron';

export interface Patron {
  readonly id: number;

  readonly googleUid: string;

  /*
    Most-recent APPROVED (not yet ACCEPTED) email verification for this patron - lets a device
    resume registration at the create-password step. Populated only by pin-login and GET /v5/patron.
  */
  readonly validatedVerificationUUID: string;

  /*
    Outstanding email change (new email awaiting verification). Undefined means the patron has no outstanding email change.
    Populated only by GET /v5/patron.
  */
  readonly pendingEmailChange: PendingEmailChange | undefined;

  readonly branchId: number;
  readonly defaultHoldPickupBranchId: number;
  readonly patronCategoryId: number;

  readonly barcode: string;

  readonly email: string;
  readonly notificationEmail: string;

  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly preferredName: string;
  readonly suffix: string;

  readonly dateOfBirth: DateOnlyIsoString;
  readonly accountExpiration: DateOnlyIsoString;

  readonly phone1: string;
  readonly phone1Ext: string;
  readonly phone1Type: string;
  readonly phone1Mobile: boolean;

  readonly phone2: string;
  readonly phone2Ext: string;
  readonly phone2Mobile: boolean;

  readonly phoneSms: string;
  readonly phoneCall: string;

  readonly address: string;
  readonly address2: string;
  readonly city: string;
  readonly state: State;
  readonly zipCode: string;

  readonly emailVerified: boolean;
  readonly pinGenerated: boolean;
  readonly optInReadingHistory: boolean;
  readonly oneClickHoldsEnabled: boolean;
  readonly lookupAttempts: number;

  readonly underBalanceLimit: boolean;

  readonly pronouns: Pronoun;
  readonly category: PatronCategory;
  readonly addresses: PatronAddress[];
  readonly openHolds: number;

  readonly openLoans: number;
  readonly accountBalance: number;
  readonly patronRule: PatronRule;

  readonly patronStatistics: PatronStatistics;
}

export interface PendingEmailChange {
  readonly email: string;   // The new, not yet verified, email
  readonly verificationUUID: string;
  readonly expiresAt: DateTimeIsoString;
  readonly expired: boolean;  // Once expired the verification link no longer works and a new verification email must be requested
}

export interface PatronCategory {
  readonly id: number;
  readonly organizationId: number;
  readonly name: string;
  readonly chipColor: string;
  readonly guarantorRequired: boolean;
  readonly dobRequired: boolean;
  readonly boundaryRestricted: boolean;
  readonly expireAtMaxAge: boolean;
  readonly expirationDate: DateOnlyIsoString;
  readonly minAge: number;
  readonly maxAge: number;
  readonly registrationTerm: number;
  readonly registrationFee: number;
  readonly renewalFee: number;
  readonly holdFee: number;
  readonly overdueNotice: boolean;
  readonly showLostItems: boolean;
  readonly active: boolean;
  readonly created: DateTimeIsoString;
  readonly modified: DateTimeIsoString;
}

export interface PatronAddress {
  readonly id: number;
  readonly patronId: number;
  readonly addressTypeId: number;
  readonly addressType: AddressType;
  readonly address: string;
  readonly address2: string;
  readonly city: string;
  readonly state: State;
  readonly zipCode: string;
  readonly country: string;
  readonly verified: boolean;
  readonly inDistrict: boolean;
  readonly primary: boolean;
}

export interface PatronStatistics {
  readonly openHolds: number;
  readonly openLoans: number;
  readonly claimedReturnedCount: number;
  readonly heldItems: number;
  readonly overdueItems: number;
}

export interface PatronRule {
  readonly id: number;
  readonly organizationId: number;
  readonly patronId: number;
  readonly patronCategoryId: number;
  readonly maxItemsOut: number;
  readonly maxItemsOutBorrowBlocked: boolean;
  readonly maxItemsOutRenewalBlocked: boolean;
  readonly maxItemsOutHoldBlocked: boolean;
  readonly maxItemsOutBlockNote: string;
  readonly maxItemsLost: number;
  readonly maxItemsLostBorrowBlocked: boolean;
  readonly maxItemsLostRenewalBlocked: boolean;
  readonly maxItemsLostHoldBlocked: boolean;
  readonly maxItemsLostBlockNote: string;
  readonly maxItemsOverdue: number;
  readonly maxItemsOverdueBorrowBlocked: boolean;
  readonly maxItemsOverdueRenewalBlocked: boolean;
  readonly maxItemsOverdueHoldBlocked: boolean;
  readonly maxItemsOverdueBlockNote: string;
  readonly maxFeeBalance: number;
  readonly maxFeeBalanceBorrowBlocked: boolean;
  readonly maxFeeBalanceRenewalBlocked: boolean;
  readonly maxFeeBalanceHoldBlocked: boolean;
  readonly maxFeeBalanceBlockNote: string;
  readonly maxHoldsPlaced: number;
  readonly maxPausedHoldsPlaced: number;
  readonly expiredCardHoldBlocked: boolean;
  readonly holdsLimitOnePerCatalogPerPatron: boolean;
  readonly maxHoldsNote: string;
  readonly active: boolean;
  readonly created: DateTimeIsoString;
  readonly modified: DateTimeIsoString;

  readonly patronCategory: PatronCategory;
}

export interface CheckoutSession {
  readonly id: number;
  readonly patronId: number;
  readonly pdfReceiptServingUrl: string;
  readonly pdfFileUri: string;
  readonly circulationItemCount: number;
  readonly active: boolean;
  readonly created: string;
  readonly modified: string;
  readonly checkoutSessionCirculationItems: CheckoutSessionCirculationItem[];
}

export interface CheckoutSessionCirculationItem {
  readonly sessionId: number;
  readonly circulationItemId: number;
  readonly barcode: string;
  readonly title: string;
  readonly mediaType: string;
  readonly dueDate: string;
  readonly dateAdded: string;
}

export interface ItemRequestUsage {
  monthlyLimit: number;
  monthlyCount: number;
}

export class PatronAccountStatus {
  accountExpiration: DateOnlyIsoString;
  currentAccountBalance: number;
  depositBalance: number;
  walletBalance: number;
  maximumAccountBalance: number;
  minimumBalanceDue: number;
  activeHoldsCount: number; // The API supplies this, but we ignore it and calculate it in a selector so that we control the meaning of "active holds".
  activeHoldCountsByStatus: RawActiveHoldCountsByStatus;
  blocks: string[];
  unresolvedCheckoutCount: number;
  unresolvedItemRequestCount: number;
  underItemRequestLimit: boolean;
  monthlyItemRequestLimit: number;
  itemRequestUsage: ItemRequestUsage;
}

export type ActiveHoldCountsByStatus = Record<HoldStatus, number>;
export type RawActiveHoldCountsByStatus = Partial<ActiveHoldCountsByStatus>; // The API omits zero-count statuses, so we need this looser type for the response.

/*
  NOTE: Even though not all of these properties are editable on the My Profile page, they need to exist in the form to preserve their values when we
  send them to the API during a patron update.

  In addition, a couple of these properties are also in EditNotificationPreferencesValue -- which is used on the Settings page -- and are sent
  separately to a different endpoint.  However, they still need to be here for the same reason as the rest of the ones that aren't on the My Profile page.
*/
export const EDIT_PATRON_BODY_PROPERTIES = [
  'preferredName', 'phone1', 'phone1Ext', 'phone1Mobile', 'phone2', 'phone2Ext', 'phone2Mobile', 'defaultHoldPickupBranchId',
  'notificationEmail', 'phoneSms', 'phoneCall', 'optInReadingHistory', 'oneClickHoldsEnabled'
] as const;

export type EditPatronValue = Pick<Patron, typeof EDIT_PATRON_BODY_PROPERTIES[number]> & {
  pronounId?: number
};

export interface PatronReferenceOptions {
  readonly branches: Branch[];
  readonly patronCategories: PatronCategory[];
  readonly pronouns: Pronoun[];
  readonly addressTypes: AddressType[];
}

export const CHANGE_PATRON_EMAIL_PROPERTIES = [
  'email'
] as const;

export type ChangePatronEmailValue = Pick<Patron, typeof CHANGE_PATRON_EMAIL_PROPERTIES[number]>;

export interface EmailLoginValue {
  readonly email: string;
  readonly password: string;
}

export interface CardLoginValue {
  readonly cardNumber: string;
  readonly pin: string;
}

export interface PatronNotificationPreferences {
  readonly notifications: NotificationPreference[];
  readonly phoneSms: string;
  readonly notificationEmail: string;
  readonly smsTermsAccepted: boolean;
}

export interface NotificationPreference {
  readonly id: number;
  readonly patronId: number;
  readonly notificationType: PatronNotificationPreferenceType;
  readonly sendNotification: boolean;
  readonly notifyEmail: boolean;
  readonly notifyText: boolean;
  readonly notifyPhone: boolean;
  readonly active: boolean;
  readonly created: DateTimeIsoString;
  readonly modified: DateTimeIsoString;
}

export type PatronNotificationPreferenceType = 'ACCOUNT' | 'AUTO_RENEW' | 'CARD_EXPIRE' | 'RECEIPT' | 'HOLD' | 'NOTICE' | 'ITEM_REQUEST';
const BASE_NOTIFICATION_PREFERENCE_VALUES = ['Email', 'SMS'] as const;
const IN_APP_ONLY_NOTIFICATION_PREFERENCE_VALUE = 'In-App Only' as const;

export const STANDARD_PATRON_NOTIFICATION_PREFERENCE_VALUES = [
  ...BASE_NOTIFICATION_PREFERENCE_VALUES, IN_APP_ONLY_NOTIFICATION_PREFERENCE_VALUE
] as const;

export type StandardPatronNotificationPreferenceValue = (typeof STANDARD_PATRON_NOTIFICATION_PREFERENCE_VALUES)[number];

export const STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS: SelectOptions<StandardPatronNotificationPreferenceValue> =
  STANDARD_PATRON_NOTIFICATION_PREFERENCE_VALUES.map(value => ({
    displayName: value, value
  }));

export const EXTENDED_PATRON_NOTIFICATION_PREFERENCE_VALUES = [
  ...BASE_NOTIFICATION_PREFERENCE_VALUES, 'Phone', IN_APP_ONLY_NOTIFICATION_PREFERENCE_VALUE
] as const;

export type ExtendedPatronNotificationPreferenceValue = (typeof EXTENDED_PATRON_NOTIFICATION_PREFERENCE_VALUES)[number];

export const EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS: SelectOptions<ExtendedPatronNotificationPreferenceValue> =
  EXTENDED_PATRON_NOTIFICATION_PREFERENCE_VALUES.map(value => ({
    displayName: value, value
  }));

export interface EditNotificationPreferencesValue {
  readonly phoneSms: string;
  readonly notificationEmail: string;
  readonly accountNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly renewNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly expirationNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly receiptNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly holdNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly noticeNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly itemRequestNotifications: ExtendedPatronNotificationPreferenceValue;
  readonly smsTermsAccepted: boolean;
}


export interface PatronRelationships {
  guardians: RelatedPatron[];
  dependents: RelatedPatron[];
  linkedAccounts: RelatedPatron[];
}

export interface RelatedPatron {
  readonly firstName: string;
  readonly lastName: string;
  readonly patronId: number;
  readonly patronType: string;
  readonly patronTypeBalanceLimit: number;
  readonly blockCount: number;
  readonly itemsOutCount: number;
  readonly overdueCount: number;
  readonly activeHoldCount: number;
  readonly readyForPickupCount: number;
  readonly accountBalance: number;
  readonly accountExpiration: DateOnlyIsoString;
}

export interface PatronSettingsQuickTogglesValue {
  readonly optInReadingHistory: boolean;
  readonly oneClickHoldsEnabled: boolean;
}

export interface EmailLoginValue {
  readonly email: string;
  readonly password: string;
}

export interface CardLoginValue {
  readonly cardNumber: string;
  readonly pin: string;
}
